// AIE-531: feature slot defaults + 5-slot normalization (Phase 4) + conditional fN_* emission
// into request.parameters (Phase 5). Emission is conditional — only slots that deviate from the
// canonical defaults or are ranged produce keys, so an all-default study stays byte-identical.
// mergeForExport is the unit-testable mirror of the TV-bridge export merge (wired in Phase 7).
import type { Feature } from "@/components/feature-builder/FeatureBuilder";
import type { FeatureTypeSpec, SlotDefault } from "@/types/api";

// Offline fallback — MUST mirror backend DEFAULT_FEATURE_SPECS (f1 RSI 14/2 … f5 RSI 9/2).
export const CANONICAL_SLOT_DEFAULTS: SlotDefault[] = [
  { slot: "f1", type: "RSI", period: 14, multiplier: 2 },
  { slot: "f2", type: "WT", period: 10, multiplier: 11 },
  { slot: "f3", type: "CCI", period: 20, multiplier: 2 },
  { slot: "f4", type: "ADX", period: 20, multiplier: 2 },
  { slot: "f5", type: "RSI", period: 9, multiplier: 2 },
];

/**
 * Lock the feature list to EXACTLY 5 positional slots (f1..f5): preserve existing slots by
 * position, pad missing trailing slots from the canonical slot default, drop any extras.
 * No add/remove/reorder — variable slot count is AIE-532. Applied at every feature-ingress
 * path (initial state, prefilled config, LC-settings import).
 */
export function normalizeToFiveSlots(
  features: Feature[],
  slotDefaults: SlotDefault[],
): Feature[] {
  const defs =
    slotDefaults.length === 5 ? slotDefaults : CANONICAL_SLOT_DEFAULTS;
  return defs.map((def, i) => {
    const existing = features[i];
    if (existing) return existing;
    // Pad with the slot's canonical default; ADX carries no second param.
    const padded: Feature = {
      id: String(i + 1),
      name: def.type,
      period: def.period,
      icon: null,
      color: "",
    };
    if (def.type !== "ADX") {
      padded.period2 = def.multiplier;
    }
    return padded;
  });
}

/**
 * AIE-534: clamp the feature list to the hard 2..15 bounds. Truncate anything past 15; pad a list
 * shorter than 2 up to the floor of 2 from the canonical slot defaults (the floor only ever pads
 * positions 0/1, both within the 5 canonical slots). A 2..15 list is returned unchanged. The input
 * array is never mutated — slice() always returns a NEW array. Padded slots mirror
 * normalizeToFiveSlots' padding shape (stable positional id; non-ADX period2 = canonical multiplier).
 * normalizeToFiveSlots stays in place; Phase 5 swaps the call sites onto this clamp.
 */
export function clampFeatureSlots(
  features: Feature[],
  slotDefaults: SlotDefault[],
): Feature[] {
  const MIN = 2;
  const MAX = 15;
  const defs =
    slotDefaults.length === 5 ? slotDefaults : CANONICAL_SLOT_DEFAULTS;
  // slice() returns a fresh array even when no truncation is needed, so the input is never mutated.
  const out = features.slice(0, MAX);
  // Padded floor slots must never collide with an existing feature's id (e.g. a single existing
  // feature already carrying id "2" would alias the positional String(2) pad). Track used ids and
  // keep the positional id when it is free, else mint a unique non-positional fallback.
  const usedIds = new Set(out.map((f) => f.id));
  for (let i = out.length; i < MIN; i++) {
    // defs is always length 5 and the floor only pads positions 0/1, so def is always present; the
    // guard exists only to satisfy noUncheckedIndexedAccess.
    const def = defs[i] ?? CANONICAL_SLOT_DEFAULTS[i];
    if (!def) break;
    let id = String(i + 1);
    if (usedIds.has(id)) {
      let n = MAX + 1; // start past the positional range so the fallback can't re-collide
      while (usedIds.has(`pad-${n}`)) n++;
      id = `pad-${n}`;
    }
    usedIds.add(id);
    const padded: Feature = {
      id,
      name: def.type,
      period: def.period,
      icon: null,
      color: "",
    };
    if (def.type !== "ADX") {
      padded.period2 = def.multiplier;
    }
    out.push(padded);
  }
  return out;
}

const TYPES_WITH_SECOND_PARAM = new Set(["RSI", "WT", "CCI"]); // ADX has none

export type RangeSpec = {
  type: "int";
  low: number;
  high: number;
  step: number;
};

export interface SlotState {
  type: string;
  values: { period: number; multiplier?: number };
  ranges: { period?: RangeSpec; multiplier?: RangeSpec };
}

// Per-param UI state, modelled on PinnableRangeSlider (value: scalar | [lo,hi]; isPinned).
export type ParamUiState = {
  value: number | [number, number];
  pinned: boolean;
};
// slotId -> wire_role -> state (Phase 6 range UI).
export type FeatureParamState = Record<string, Record<string, ParamUiState>>;

function deviates(slot: SlotState, def: SlotDefault): boolean {
  if (slot.type !== def.type) return true;
  if (slot.values.period !== def.period) return true;
  if (TYPES_WITH_SECOND_PARAM.has(slot.type)) {
    // An omitted 2nd param normalizes to the slot default before comparing.
    if ((slot.values.multiplier ?? def.multiplier) !== def.multiplier) {
      return true;
    }
  }
  if (slot.ranges.period || slot.ranges.multiplier) return true;
  return false;
}

// Codex (AIE-531 Phase 4 review): a re-typed slot whose 2nd param was never set must emit the
// TYPE's smoothing default (keyed by slot.type), NOT the slot-POSITION default — e.g. f2 (WT,
// position default 11) re-typed to RSI must emit 2, not 11.
function multiplierDefaultForType(
  type: string,
  featureTypes: Record<string, FeatureTypeSpec>,
  slotPositionDefault: number,
): number {
  const spec = featureTypes[type]?.params.find(
    (p) => p.wire_role === "multiplier",
  );
  return spec?.default ?? slotPositionDefault;
}

/**
 * Flat dict of fN_* keys to MERGE into processedParameters (-> request.parameters), in slot order
 * f1..fN, ONLY for deviating/ranged slots (within the 5 canonical positions) or for any added slot
 * (AIE-534). Empty when an all-default 5-slot study (so default studies stay byte-identical). A
 * non-5 slot count emits `feature_count` so the backend knows the active arity. A ranged param ->
 * pre-formed Optuna spec; companions are fixed scalars. At a canonical position ADX always carries
 * the preserved legacy multiplier and never a user-editable/ranged 2nd param; an ADDED ADX slot
 * (i>=5) emits type+period only (no multiplier).
 *
 * Precondition: `slots` is expected pre-clamped to the 2..15 bounds. The exported seam is
 * buildOptimizationRequest; current production usage clamps via NewStudyModern (clampFeatureSlots).
 * buildFeatureParams itself does not re-clamp.
 */
export function buildFeatureParams(
  slots: SlotState[],
  slotDefaults: SlotDefault[],
  featureTypes: Record<string, FeatureTypeSpec>,
): Record<string, unknown> {
  const defs =
    slotDefaults.length === 5 ? slotDefaults : CANONICAL_SLOT_DEFAULTS;
  const out: Record<string, unknown> = {};
  // AIE-534: a non-default arity is communicated via feature_count; a default 5-slot study omits
  // it so the all-default request stays byte-identical.
  if (slots.length !== 5) {
    out.feature_count = slots.length;
  }
  slots.forEach((slot, i) => {
    const def = defs[i]; // canonical default for f1..f5; undefined for added slots (i>=5)
    const key = `f${i + 1}`; // positional wire prefix (def.slot only covers f1..f5)
    const hasSecondParam = TYPES_WITH_SECOND_PARAM.has(slot.type);
    if (def) {
      // Canonical position: emit only when the slot deviates from its default.
      if (!deviates(slot, def)) return;
      out[`${key}_type`] = slot.type;
      out[`${key}_period`] = slot.ranges.period ?? slot.values.period;
      if (hasSecondParam) {
        out[`${key}_multiplier`] =
          slot.ranges.multiplier ??
          slot.values.multiplier ??
          multiplierDefaultForType(slot.type, featureTypes, def.multiplier);
      } else {
        // ADX at a canonical position: legacy default preserved for export.
        out[`${key}_multiplier`] = def.multiplier;
      }
    } else {
      // Added slot (i>=5): no canonical default, so always emit type+period.
      out[`${key}_type`] = slot.type;
      out[`${key}_period`] = slot.ranges.period ?? slot.values.period;
      if (hasSecondParam) {
        // RSI/WT/CCI: range -> value -> the TYPE catalog default.
        out[`${key}_multiplier`] =
          slot.ranges.multiplier ??
          slot.values.multiplier ??
          multiplierDefaultForType(slot.type, featureTypes, 1);
      }
      // ADDED ADX slot: type+period only, no multiplier.
    }
  });
  return out;
}

/**
 * AIE-534 Phase 6: the TradingView bridge serializer (serializeLcSettings) currently emits EXACTLY
 * 5 feature slots (f1..f5). A study can round-trip through it iff its active feature_count is 5 —
 * or absent, which means a default/legacy 5-slot study. Any other arity (e.g. 3 or 7) cannot be
 * serialized today, so TV/TOML export must be blocked for it. Fail safe: a malformed/NaN count is
 * treated as not exportable so an unknown arity is never exported.
 */
export function isTvExportable(fixedValues: Record<string, unknown>): boolean {
  const raw = fixedValues.feature_count;
  // Absent (no key / undefined) -> a default/legacy 5-slot study is exportable. A PRESENT value is
  // exportable only when it is exactly the integer 5; anything else (null, "5", true, 7, NaN,
  // non-integer) fails closed — the TV bridge serializes exactly 5 slots, never an unknown arity.
  if (raw === undefined) return true;
  return typeof raw === "number" && Number.isInteger(raw) && raw === 5;
}

/** Mirrors ExportForTvButton's merge+filter so it is unit-testable (wired in Phase 7). */
export function mergeForExport(
  lcDefaults: Record<string, unknown>,
  fixedValues: Record<string, unknown>,
  bestParams: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...lcDefaults, ...fixedValues, ...bestParams };
  const lcKeys = new Set(Object.keys(lcDefaults));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(merged)) {
    if (lcKeys.has(k)) out[k] = v;
  }
  return out;
}

/**
 * Map the per-param range UI state (Phase 6) onto SlotState[] for buildFeatureParams. A PINNED
 * param contributes a scalar (any prior [lo,hi] collapses to its low and the range is DROPPED, so
 * repinning removes the emitted spec); an UNPINNED param contributes an int range spec. Params with
 * no UI state fall back to the feature's scalar (period / period2) or the type catalog default.
 */
export function deriveSlotStates(
  features: { id: string; name: string; period: number; period2?: number }[],
  paramState: FeatureParamState,
  featureTypes: Record<string, FeatureTypeSpec>,
): SlotState[] {
  return features.map((f) => {
    const values: SlotState["values"] = { period: f.period };
    const ranges: SlotState["ranges"] = {};
    for (const p of featureTypes[f.name]?.params ?? []) {
      const st = paramState[f.id]?.[p.wire_role];
      const fallbackScalar =
        p.wire_role === "period" ? f.period : (f.period2 ?? p.default);
      if (!st || st.pinned) {
        const scalar = st
          ? Array.isArray(st.value)
            ? st.value[0]
            : st.value
          : fallbackScalar;
        if (p.wire_role === "period") {
          values.period = scalar;
        } else {
          values.multiplier = scalar;
        }
      } else {
        const [low, high] = st.value as [number, number];
        ranges[p.wire_role] = { type: "int", low, high, step: p.step ?? 1 };
      }
    }
    return { type: f.name, values, ranges };
  });
}

/**
 * Codex (AIE-534 PR review): the display/storage TOML must stay in sync with what is actually
 * optimized. Build it from the SAME derived slot state used for `request.parameters`
 * (`deriveSlotStates`), NOT the raw `features` scalars — otherwise a flag-ON pinned-slider edit
 * reaches the optimizer (via `parameters`) while the stored / downloaded / AI-prefilled TOML keeps
 * the stale scalar. The scalar `[[features]]` TOML can't express a range, so a ranged param falls
 * back to a representative scalar (the range low). A default 5-slot study (no edits) maps back to
 * the original feature scalars, so its TOML is unchanged.
 */
export function slotStatesToTomlFeatures(
  slots: SlotState[],
): { name: string; period: number; period2?: number }[] {
  return slots.map((s) => {
    const multiplier = s.values.multiplier ?? s.ranges.multiplier?.low;
    return {
      name: s.type,
      period: s.values.period,
      ...(multiplier !== undefined ? { period2: multiplier } : {}),
    };
  });
}
